import React, { forwardRef, useImperativeHandle } from 'react';
import SelectedOptions from './SelectedOptions';
import OptionsList from './OptionsList';
import styles from './index.module.scss';
import cx from 'classnames';
import { IOption, IAutoCompleteProps, InputStates } from './interfaces';
import { ArrowDownSolidIcon, ArrowUpSolidIcon, CloseIcon } from '@navi/web-ui/lib/icons';
import { useOutsideClick } from '@navi/web-ui/lib/hooks';
import { Typography } from '@navi/web-ui/lib/primitives';

const AutocompleteDropdown = forwardRef<HTMLInputElement, IAutoCompleteProps>(
  (
    {
      options,
      selectedOptions,
      containerClasses,
      placeholder = 'Type here to search',
      showLoader,
      maxSelectedChipToShow = 4,
      error,
      maxOptionsToSelect,
      disabled,
      disableSearch = false,
      customOptionTemplate,
      onSearch,
      onSelectionChange,
      onBlur,
      selectedOptionWrapperClass = '',
      selectedOptionLabelClass = '',
      selectedOptionChipIconColor,
      disableClearSearchOnOutSideClick = false,
      ...restInputProps
    },
    ref
  ) => {
    AutocompleteDropdown.displayName = 'AutoCompleteDropdown';
    const [searchText, setSearchText] = React.useState('');
    const inputRef = React.useRef<HTMLInputElement>(null);
    const autocompleteRef = React.useRef<HTMLInputElement>(null);
    const [inputState, setInputState] = React.useState<InputStates>(
      error ? InputStates.ERROR : InputStates.DEFAULT
    );
    const [showPicker, setShowPicker] = React.useState(false);
    const [showError, setShowError] = React.useState(error);

    // If component is focused, set the input state to active
    React.useEffect(() => {
      if (inputState === InputStates.ACTIVE) {
        inputRef?.current?.focus();
      }
    }, [inputState]);
    // Expose the inputRef to the parent component
    useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);

    useOutsideClick(autocompleteRef, () => {
      if (showPicker) {
        togglePicker();
      }
      if (!disableClearSearchOnOutSideClick) onSearch?.('');
      setSearchText('');
      setInputState(InputStates.DEFAULT);
    });

    React.useEffect(() => {
      if (maxOptionsToSelect) {
        checkMaxOptionsToSelectValidation(selectedOptions);
      }
    }, [maxOptionsToSelect]);

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      if (disableSearch) return;
      const searchText = event.target.value;
      onSearch?.(searchText);
      setSearchText(searchText);

      if (!showPicker) {
        togglePicker();
      }

      if (inputState !== InputStates.ACTIVE) {
        setInputState(InputStates.ACTIVE);
      }
    };

    const checkMaxOptionsToSelectValidation = (updatedSelection: IOption[]) => {
      let isValid = true;
      if (!maxOptionsToSelect) {
        return isValid;
      }
      if (showError) {
        setShowError('');
        setInputState(InputStates.DEFAULT);
      }
      if (typeof maxOptionsToSelect === 'number') {
        if (updatedSelection.length > maxOptionsToSelect) {
          setInputState(InputStates.ERROR);
          setShowPicker(false);
          setShowError(`You can select max ${maxOptionsToSelect} options`);
          isValid = false;
        }
      } else {
        if (updatedSelection.length > maxOptionsToSelect.count) {
          setInputState(InputStates.ERROR);
          setShowPicker(false);
          setShowError(maxOptionsToSelect.message);
          isValid = false;
        }
      }
      return isValid;
    };

    const handleOptionClick = (option: IOption) => {
      if (disabled) {
        return;
      }
      const isOptionSelected = selectedOptions.some(
        selectedOption => selectedOption.value === option.value
      );

      // Toggle selection state for the clicked option
      let updatedSelection = [];
      if (isOptionSelected) {
        updatedSelection = selectedOptions.filter(
          selectedOption => selectedOption.value !== option.value
        );
      } else {
        updatedSelection = [...selectedOptions, option];
        // Check if the max selection count is valid
        const isValid = checkMaxOptionsToSelectValidation(updatedSelection);
        if (!isValid) {
          return;
        }
      }

      // Clear the input field when a new option is selected
      setSearchText('');

      // Callback to the consumer component to handle selection change
      onSelectionChange(updatedSelection);
    };

    const togglePicker = () => {
      setShowPicker(!showPicker);
    };

    const isOptionSelected = (option: IOption) =>
      selectedOptions.some(selectedOption => selectedOption.value === option.value);

    const handleContainerClick = () => {
      if (disabled) {
        return;
      }
      if (!showPicker) {
        setInputState(InputStates.ACTIVE);
      } else {
        setInputState(InputStates.DEFAULT);
        onSearch?.('');
      }
      setShowError('');
      togglePicker();
    };

    const handleClearAll = (event: React.MouseEvent<SVGSVGElement, MouseEvent>) => {
      if (disabled) {
        return;
      }
      event.stopPropagation();
      onSelectionChange([]);
      setShowError('');
      setInputState(InputStates.DEFAULT);
    };

    const ArrowIcon = showPicker ? ArrowUpSolidIcon : ArrowDownSolidIcon;

    return (
      <div className={cx(styles.container, containerClasses)} ref={autocompleteRef}>
        <div
          className={cx(
            styles.autocompleteDropdown,
            {
              [styles.disabled]: disabled
            },
            {
              [styles.active]: inputState === InputStates.ACTIVE,
              [styles.error]: inputState === InputStates.ERROR,
              [styles.default]: inputState === InputStates.DEFAULT
            }
          )}
          onClick={handleContainerClick}
        >
          <div className={styles.inputContainer}>
            <SelectedOptions
              maxSelectedChipToShow={maxSelectedChipToShow}
              selectedOptions={selectedOptions}
              onOptionClick={handleOptionClick}
              disabled={disabled}
              wrapperClasses={selectedOptionWrapperClass}
              labelClasses={selectedOptionLabelClass}
              IconColor={selectedOptionChipIconColor}
            />
            <input
              ref={inputRef}
              placeholder={placeholder}
              type="text"
              value={searchText}
              onChange={handleInputChange}
              disabled={disabled}
              {...restInputProps}
              readOnly={disableSearch}
            />
          </div>
          <div className={styles.iconsContainer}>
            {selectedOptions?.length ? (
              <CloseIcon className={styles.clearAll} onClick={handleClearAll} />
            ) : null}
            <ArrowIcon width={9} />
          </div>
        </div>
        {showError ? (
          <Typography variant="p4" className={styles.error}>
            {showError}
          </Typography>
        ) : null}
        {showPicker ? (
          <OptionsList
            options={options}
            isOptionSelected={isOptionSelected}
            onOptionClick={handleOptionClick}
            optionTemplate={customOptionTemplate}
            showLoader={showLoader}
            isMultiSelect
          />
        ) : null}
      </div>
    );
  }
);

export default AutocompleteDropdown;
